import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
    parseWidgetData,
    parseReverseGeoData,
    parseUpdateAddressData,
    parseAddressById,
    parseItemReviewList,
} from "./parser"
import { handleAuthentication } from "@containers/App/reducer"
import { setRehashFlag } from "@containers/App/ABExperimentReducer/reducer.js"
import { showSnackBar } from "@containers/App/reducer"

const initialState = {
    isLoadingData: false,
    isFetched: false,
    error: null,
    widgets: {},
    updateAddressDialogBox: {},
    selectedAddressId: null,
    addressCta: {},
    reverseGeoData: {},
    addressSelectionLoading: false,
    addressSelectionError: false,
    removeAddressError: false,
    pincodePending: false,
    verifiedPincode: {},
    pincodeError: false,
    addressById: {},
    itemReviewList: {},
    totalNumerOfAddress: 0,
    hasMore: false,
    currentPageNumber: 0,
    isFetchedMore: false,
    isFetchingMore: false,
    isFetchMoreError: false,
}

export const DioAddressPageSlice = createSlice({
    name: "dioAddressPage",
    initialState: initialState,
    reducers: {
        reset: () => initialState,
        setAddressSelectionLoading: (state, action) => {
            state.addressSelectionLoading = action.payload
        },
    },
    extraReducers: (builder) => {
        //handle asynchronous actions
        builder.addCase(fetchAddressData.fulfilled, (state, action) => {
            state.isLoadingData = false
            state.isFetched = true
            state.error = false
            const data = parseWidgetData(action?.payload?.widgets)
            state.widgets = data?.widget
            state.selectedAddressId = data?.selectedAddressId
            state.addressCta = data?.addressCta
            state.totalNumerOfAddress = data?.totalNumerOfAddress
            state.updateAddressDialogBox = parseUpdateAddressData(action?.payload?.update_address_dialog_data)
            state.currentPageNumber = data?.currentPageNumber
            state.hasMore = data?.hasMore
        }),
            builder.addCase(fetchAddressData.pending, (state) => {
                state.isLoadingData = true
                state.isFetched = false
                state.error = false
                state.selectedAddressId = null
                state.addressCta = {}
            }),
            builder.addCase(fetchAddressData.rejected, (state) => {
                state.isLoadingData = false
                state.error = true
                state.isFetched = false
                state.selectedAddressId = null
                state.addressCta = {}
            })
        builder.addCase(fetchMoreAddressData.fulfilled, (state, action) => {
            state.isFetchedMore = true
            state.isFetchingMore = false
            state.isFetchMoreError = false
            const data = parseWidgetData(
                action?.payload?.response?.widgets,
                action?.payload?.pageNumber,
                state.totalNumerOfAddress,
                action?.payload?.existingAddress,
                action?.payload?.addressCta,
                action?.payload?.addressIdSelected
            )
            state.widgets = data?.widget
            state.selectedAddressId = data?.selectedAddressId
            state.addressCta = data?.addressCta
            state.totalNumerOfAddress = data?.totalNumerOfAddress
            state.updateAddressDialogBox = parseUpdateAddressData(action?.payload?.update_address_dialog_data)
            state.currentPageNumber = data?.currentPageNumber
            state.hasMore = data?.hasMore
        }),
            builder.addCase(fetchMoreAddressData.pending, (state) => {
                state.isFetchedMore = false
                state.isFetchingMore = true
                state.isFetchMoreError = false
                state.error = false
            }),
            builder.addCase(fetchMoreAddressData.rejected, (state) => {
                state.isFetchedMore = false
                state.isFetchingMore = false
                state.isFetchMoreError = true
                state.error = true
            })
        builder.addCase(fetchAddressDataById.fulfilled, (state, action) => {
            state.addressById = parseAddressById(action?.payload?.response?.widgets?.[0])
            if (typeof action?.payload?.successCallback === "function") {
                action?.payload?.successCallback(state.addressById)
            }
        })
        builder.addCase(getSearchedAddress.fulfilled, (state, action) => {
            state.reverseGeoData = parseReverseGeoData(action?.payload)
            state.addressSelectionLoading = false
            state.addressSelectionError = false
        })
        builder.addCase(getSearchedAddress.pending, (state) => {
            state.addressSelectionLoading = true
            state.addressSelectionError = false
        })
        builder.addCase(getSearchedAddress.rejected, (state) => {
            state.reverseGeoData = {}
            state.addressSelectionLoading = false
            state.addressSelectionError = true
        })
        builder.addCase(removeAddress.rejected, (state) => {
            state.removeAddressError = true
        })
        builder.addCase(fetchPinCodeDetails.fulfilled, (state, action) => {
            state.pincodePending = false
            state.verifiedPincode = {
                id: action?.payload?.id,
                city: action?.payload?.city,
                state: action?.payload?.state,
            }
            state.pincodeError = false
        })
        builder.addCase(fetchPinCodeDetails.pending, (state) => {
            state.pincodePending = true
            state.pincodeError = false
        })
        builder.addCase(fetchPinCodeDetails.rejected, (state) => {
            state.pincodePending = false
            state.verifiedPincode = {}
            state.pincodeError = true
        })
        builder.addCase(getReviewItems.fulfilled, (state, action) => {
            state.itemReviewList[action?.payload?.addressById] = parseItemReviewList(
                action?.payload?.response
            )
        })
    },
})

export const fetchAddressData = createAsyncThunk(
    "dioAddressPage/fetchAddressPageData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const sourcePage = sessionStorage.getItem("pageSource")
        try {
            const res = await api.get(
                `/api/v4/carts/addresses?page_number=0&page_size=10&types=done_in_one${`${
                    sourcePage ? `&request_page_source=${sourcePage}` : ``
                }`}`
            )
            dispatch(setRehashFlag(res?.data?.rehash_data?.is_enabled))
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const fetchMoreAddressData = createAsyncThunk(
    "dioAddressPage/fetchMoreAddressPageData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { pageNumber, existingAddress, addressCta, addressIdSelected } = _arg
        const sourcePage = sessionStorage.getItem("pageSource")
        try {
            const res = await api.get(
                `/api/v4/carts/addresses?page_number=${pageNumber}&page_size=10&types=done_in_one${`${
                    sourcePage ? `&request_page_source=${sourcePage}` : ``
                }`}`
            )
            dispatch(setRehashFlag(res?.data?.rehash_data?.is_enabled))
            return Promise.resolve({
                response: res.data,
                pageNumber: pageNumber,
                existingAddress: existingAddress,
                addressCta: addressCta,
                addressIdSelected: addressIdSelected,
            })
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const fetchAddressDataById = createAsyncThunk(
    "dioAddressPage/fetchAddressPageDataById",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const addressData = _arg

        try {
            const res = await api.get(`api/v4/carts/addresses/${addressData?.addressId}?types=done_in_one`)
            return Promise.resolve({ response: res.data, successCallback: addressData?.successCallback })
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const getSearchedAddress = createAsyncThunk(
    "dioAddressPage/searchAddressPageData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const latLng = _arg
        try {
            const res = await api.get(`api/v4/reverse_geocoding`, {
                params: latLng,
            })
            return Promise.resolve(res.data)
        } catch (err) {
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const removeAddress = createAsyncThunk(
    "dioAddressPage/removeAddressData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const addressData = _arg
        try {
            const res = await api.delete(
                `api/v4/carts/addresses?types=done_in_one&address_id=${addressData?.payload}`
            )
            if (typeof addressData?.successCallback === "function") {
                addressData?.successCallback()
                return Promise.resolve()
            } else {
                return Promise.resolve(res.data)
            }
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (typeof addressData?.errorCallback === "function") {
                addressData?.errorCallback(err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const addAddress = createAsyncThunk(
    "dioAddressPage/addAddressData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const addressData = _arg
        const sourcePage = sessionStorage.getItem("pageSource")
        try {
            const res = await api.post(
                `api/v4/carts/addresses?types=done_in_one${
                    sourcePage ? `&request_page_source=${sourcePage}` : ``
                }`,
                {
                    ...addressData.payload,
                }
            )
            if (typeof addressData?.successCallback === "function") {
                addressData?.successCallback()
                return Promise.resolve()
            } else {
                return Promise.resolve(res.data)
            }
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (typeof addressData?.errorCallback === "function") {
                addressData?.errorCallback(err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const editAddress = createAsyncThunk(
    "dioAddressPage/editAddressData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const addressData = _arg
        try {
            const res = await api.put(`api/v4/carts/addresses?types=done_in_one`, addressData?.payload)
            if (typeof addressData?.successCallback === "function") {
                addressData?.successCallback()
                return Promise.resolve()
            } else {
                return Promise.resolve(res.data)
            }
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (typeof addressData?.errorCallback === "function") {
                addressData?.errorCallback(err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const fetchPinCodeDetails = createAsyncThunk(
    "dioAddressPage/fetchPincodeData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const pincode = _arg
        try {
            const res = await api.get(`api/v4/pincode/details?pincode=${pincode}`)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const getReviewItems = createAsyncThunk(
    "dioAddressPage/getCartData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const addressId = _arg
        try {
            const res = await api.get(`api/v4/carts/addresses/${addressId}/oos?types=done_in_one`)
            return Promise.resolve({ response: res, addressById: addressId })
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const selectAddress = createAsyncThunk(
    "dioAddressPage/selectAddress",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const data = _arg
        try {
            const res = await api.put(`api/v4/carts/preferences?types=done_in_one`, {
                address_id: data?.payload,
            })
            if (typeof data?.successCallback === "function") {
                data?.successCallback()
                return Promise.resolve()
            } else {
                return Promise.resolve(res.data)
            }
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const dioAddressPageReducer = DioAddressPageSlice.reducer
export const { reset, setAddressSelectionLoading } = DioAddressPageSlice.actions
